<template>
  <div>
    <v-card class="pa-5">
      <v-card-text>
        <v-card-title class="d-flex align-center">
          <h4>{{ $t('reports.tireChangeReport') }}</h4>
          <v-spacer></v-spacer>

          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn @click="filterData" v-on="on" v-bind="attrs" depressed color="primary" class="mr-2" min-width="40">
                  <v-icon small>fas fa-search</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('general.search') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn @click="resetFilter" v-on="on" v-bind="attrs" depressed color="primary" class="mr-2" min-width="40">
                  <v-icon small>fa-solid fa-eraser</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('general.resetFilter') }}</span>
            </v-tooltip>

            <v-btn
              @click="download"
              :loading="loadingButton"
              :disabled="loadingTable || reportUnits.length === 0"
              color="primary"
              depressed
            >
              {{ $t('reports.downloadReport') }}
              <v-icon small class="ml-2">fas fa-download</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form v-model="valid" lazy-validation ref="form" @submit.prevent="filterData">
            <v-row dense>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model.trim="filter.plate_number"
                  :label="$t('monitoring.plaque')"
                  :rules="rules.textNoSpecialCharsFilters"
                  clearable
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model.trim="filter.unit_number"
                  :label="$t('general.noEco')"
                  :rules="rules.textNoSpecialCharsFilters"
                  clearable
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                  v-model="filter.unit_type_id"
                  :items="unitTypes"
                  item-text="alias"
                  item-value="id"
                  :label="$t('general.unitType')"
                  clearable
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                  v-model="filter.questionnaire_id"
                  :items="tests"
                  item-text="name"
                  item-value="id"
                  :label="$t('reports.checklist')"
                  clearable
                  outlined
                  dense
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                  v-model="filter.owner"
                  :items="ownerTypes"
                  item-text="text"
                  item-value="value"
                  :label="$t('general.property')"
                  clearable
                  outlined
                  dense
                />
              </v-col>
              <v-col v-if="filter.dateRange" cols="12" sm="6" md="3">
                <v-menu v-model="menuStartDate" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filter.start_date"
                      :label="$t('general.startDate')"
                      outlined
                      dense
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    no-title
                    color="primary"
                    v-model="filter.start_date"
                    @input="handleStartDateSelection"
                  />
                </v-menu>
              </v-col>
              <v-col v-if="filter.dateRange" cols="12" sm="6" md="3">
                <v-menu v-model="menuEndDate" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filter.end_date"
                      :label="$t('general.endDate')"
                      outlined
                      dense
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    no-title
                    color="primary"
                    v-model="filter.end_date"
                    @input="menuEndDate = false"
                    :allowed-dates="allowedEndDates"
                    :day-style="getDayStyle"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-checkbox
                  v-model="filter.dateRange"
                  :label="$t('general.dateRange')"
                  hide-details
                  class="mt-0"
                  color="primary"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-overlay absolute opacity="0" :value="loadingTable"/>
        <v-data-table
          :loading="loadingTable"
          :loading-text="$t('general.loading')"
          :headers="headers"
          :items="reportUnits"
          :server-items-length="totalItems"
          :options="optionsDataTable"
          :footer-props="{ 'items-per-page-options': [15, 50, 70, 100] }"
          @update:options="onOptionsChange"
          item-key="unit_id"
          show-expand
          class="elevation-1"
        >
          <template v-slot:item.plates="{ item }">
            {{ formatPlates(item.plates) }}
          </template>
          <template v-slot:item.unit_number="{ item }">
            {{ item.unit_number || 'N/A' }}
          </template>
          <template v-slot:item.unit_type="{ item }">
            {{ item.unit_type || 'N/A' }}
          </template>
          <template v-slot:item.owner="{ item }">
            {{ translateOwner(item.owner) }}
          </template>
          <template v-slot:item.owner_name="{ item }">
            {{ item.owner_name || 'N/A' }}
          </template>
          <template v-slot:item.inspections_count="{ item }">
            {{ item.inspections_count || 0 }}
          </template>
          <template v-slot:item.changes_count="{ item }">
            <strong>{{ item.changes_count || 0 }}</strong>
          </template>
          <template v-slot:item.latest="{ item }">
            <span v-if="!item.latest" class="font-weight-bold text--secondary">N/A</span>
            <span
              v-else-if="item.latest.status === 'no_previous_inspection'"
              class="font-weight-bold text--secondary"
            >
              {{ $t('reports.noPreviousInspection') }}
            </span>
            <div v-else class="d-flex align-center justify-center">
              <v-chip v-if="item.latest.changes_count > 0" small color="primary" outlined class="font-weight-bold">
                {{ $tc('reports.changesCount', item.latest.changes_count, { count: item.latest.changes_count }) }}
              </v-chip>
              <span v-else class="font-weight-bold text--secondary">{{ $t('reports.noChanges') }}</span>
              <!-- Advertencia de inspección incompleta; se deja comentada por si se retoma
              <v-tooltip v-if="item.latest.status === 'incomplete'" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-on="on" v-bind="attrs" small color="warning" class="ml-2">fas fa-exclamation-triangle</v-icon>
                </template>
                <span>{{ $t('reports.incompleteInspection') }}</span>
              </v-tooltip>
              -->
            </div>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <tire-change-report-details :unit-id="item.unit_id" :filters="detailFilters"/>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import TireChangeReportDetails from './tireChangeReportDetails.vue'

const MAX_RANGE_DAYS = 29

export default {
  name: 'tireChangeReport',
  components: {
    TireChangeReportDetails
  },
  data () {
    const filter = this.defaultFilter()

    return {
      valid: true,
      loadingTable: false,
      loadingButton: false,
      menuStartDate: false,
      menuEndDate: false,
      totalItems: 0,
      // El endpoint no recibe parámetros de orden, así que las columnas no son ordenables
      optionsDataTable: {
        itemsPerPage: 15,
        page: 1
      },
      filter,
      // tests/get escribe tests, options y loadingTable en el contexto
      options: {
        totalItems: 0,
        page: 1
      },
      // Filtros con los que se consultó la tabla: la paginación, el orden y la descarga
      // usan estos, así el Excel siempre coincide con lo que se muestra en pantalla
      appliedFilter: { ...filter },
      reportUnits: [],
      unitTypes: [],
      tests: []
    }
  },
  watch: {
    'filter.dateRange' (val) {
      this.filter.start_date = val ? this.returnStartDate() : null
      this.filter.end_date = val ? this.returnEndDate() : null
    }
  },
  computed: {
    headers () {
      return [
        { text: '', value: 'data-table-expand', sortable: false },
        { text: this.$t('monitoring.plaque'), align: 'center', value: 'plates', sortable: false },
        { text: this.$t('general.noEco'), align: 'center', value: 'unit_number', sortable: false },
        { text: this.$t('general.unitType'), align: 'center', value: 'unit_type', sortable: false },
        { text: this.$t('general.property'), align: 'center', value: 'owner', sortable: false },
        { text: this.$t('general.owne'), align: 'center', value: 'owner_name', sortable: false },
        { text: this.$t('reports.totalInspections'), align: 'center', value: 'inspections_count', sortable: false },
        { text: this.$t('reports.totalChanges'), align: 'center', value: 'changes_count', sortable: false },
        { text: this.$t('reports.lastResult'), align: 'center', value: 'latest', sortable: false }
      ]
    },
    // Se calcula una sola vez por filtro aplicado para no recargar el detalle en cada render
    detailFilters () {
      return this.filterParams()
    },
    ownerTypes () {
      return [
        { text: this.$t('units.OWN'), value: 'OWN' },
        { text: this.$t('units.EXTERNAL'), value: 'EXTERNAL' }
      ]
    },
    rules () {
      return this.$validationRules()
    }
  },
  created () {
    this.$store.dispatch('unitTypes/getAll', { event: { context: this } })
    this.$store.dispatch('tests/get', { event: { context: this, item: { perPage: -1, page: 1 } } })
    // La primera carga la dispara v-data-table al emitir update:options en su inicialización
  },
  methods: {
    defaultFilter () {
      return {
        plate_number: null,
        unit_number: null,
        unit_type_id: null,
        questionnaire_id: null,
        owner: null,
        dateRange: true,
        start_date: this.returnStartDate(),
        end_date: this.returnEndDate()
      }
    },
    returnStartDate () {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().substr(0, 10)
    },
    returnEndDate () {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().substr(0, 10)
    },
    onOptionsChange (options) {
      this.optionsDataTable = options
      this.get()
    },
    resetFilter () {
      this.filter = this.defaultFilter()
      this.$refs.form.resetValidation()
      this.applyFilter()
    },
    filterData () {
      if (!this.$refs.form.validate()) return this.showNotification(this.$t('general.errorFilterMessage'), 'warn', -1)
      this.applyFilter()
    },
    applyFilter () {
      this.appliedFilter = { ...this.filter }
      this.goToFirstPage()
    },
    goToFirstPage () {
      // Si la página cambia, la tabla emite update:options y dispara get()
      if (this.optionsDataTable.page === 1) return this.get()
      this.optionsDataTable = { ...this.optionsDataTable, page: 1 }
    },
    filterParams () {
      const filter = this.appliedFilter

      return {
        plate_number: filter.plate_number || undefined,
        unit_number: filter.unit_number || undefined,
        unit_type_id: filter.unit_type_id || undefined,
        questionnaire_id: filter.questionnaire_id || undefined,
        owner: filter.owner || undefined,
        start_date: filter.start_date || undefined,
        end_date: filter.end_date || undefined
      }
    },
    get () {
      const { page, itemsPerPage } = this.optionsDataTable

      this.loadingTable = true
      this.$store.dispatch('reports/getTireChangeReport', {
        event: {
          context: this,
          params: {
            ...this.filterParams(),
            perPage: itemsPerPage,
            page
          }
        }
      })
    },
    download () {
      this.loadingButton = true
      // El export usa los mismos filtros con los que se consultó la tabla, sin paginar
      this.$store.dispatch('reports/getTireChangeReportDoc', {
        event: {
          context: this,
          params: this.filterParams()
        }
      })
    },
    formatPlates (plates) {
      if (!plates || plates.length === 0) return 'N/A'
      return plates.join(', ')
    },
    translateOwner (value) {
      const ownerMap = {
        OWN: this.$t('units.OWN'),
        EXTERNAL: this.$t('units.EXTERNAL')
      }
      return ownerMap[value] || 'N/A'
    },
    handleStartDateSelection (date) {
      this.filter.start_date = date
      this.filter.end_date = this.addDays(date, MAX_RANGE_DAYS)
      this.menuStartDate = false
    },
    allowedEndDates (date) {
      if (!this.filter.start_date) return true
      return date >= this.filter.start_date && date <= this.addDays(this.filter.start_date, MAX_RANGE_DAYS)
    },
    addDays (isoDate, days) {
      const date = new Date(`${isoDate}T00:00:00Z`)
      date.setUTCDate(date.getUTCDate() + days)
      return date.toISOString().substr(0, 10)
    },
    getDayStyle (date) {
      if (this.allowedEndDates(date)) return null
      return { backgroundColor: 'rgba(0, 0, 0, 0.1)', color: 'gray' }
    }
  }
}
</script>
